import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { extractApiError } from "../lib/errors";
import { toast } from "sonner";
import {
  LogOut,
  CheckCircle2,
  Circle,
  Trash2,
  Calendar,
  Plus,
  ArrowUp,
  ArrowDown,
  Save,
  X,
  Heart,
  Music2,
  ListChecks,
  Star,
  Quote,
  MessageSquare,
  Eye,
  EyeOff,
  Mail,
  Users,
  ImagePlus,
} from "lucide-react";

const TABS = [
  { id: "prayer", label: "Prayer Requests", icon: Heart },
  { id: "song", label: "Song Requests", icon: Music2 },
  { id: "schedule", label: "On-Air Lineup", icon: ListChecks },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "testimonies", label: "Testimonies", icon: Quote },
  { id: "messages", label: "Contact Messages", icon: MessageSquare },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "trustees", label: "Trustees", icon: Users },
];

const emptySched = { title: "", host: "", day: "Mon", start_time: "", end_time: "", description: "" };
const emptyTrustee = { name: "", role: "Trustee", bio: "", image_url: "", published: true };
const DAYS = ["Daily", "Mon-Fri", "Weekends", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("prayer");

  const [prayers, setPrayers] = useState([]);
  const [songs, setSongs] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [trustees, setTrustees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // schedule id or "new"
  const [draft, setDraft] = useState(emptySched);
  const [trusteeEditing, setTrusteeEditing] = useState(null);
  const [trusteeDraft, setTrusteeDraft] = useState(emptyTrustee);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, s, sc, r, t, m, n, tr] = await Promise.all([
        api.get("/prayer-requests"),
        api.get("/song-requests"),
        api.get("/schedule"),
        api.get("/reviews"),
        api.get("/testimonies"),
        api.get("/contact-messages"),
        api.get("/newsletter"),
        api.get("/trustees"),
      ]);
      setPrayers(p.data);
      setSongs(s.data);
      setSchedule(sc.data);
      setReviews(r.data);
      setTestimonies(t.data);
      setMessages(m.data);
      setSubscribers(n.data);
      setTrustees(tr.data);
    } catch (e) {
      toast.error(extractApiError(e, "Could not load dashboard data."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const counts = useMemo(
    () => ({
      prayer: prayers.length,
      prayerPending: prayers.filter((p) => !p.handled).length,
      song: songs.length,
      songPending: songs.filter((s) => !s.handled).length,
      schedule: schedule.length,
      reviews: reviews.length,
      reviewsPub: reviews.filter((r) => r.published).length,
      testimonies: testimonies.length,
      testimoniesPub: testimonies.filter((t) => t.published).length,
      messages: messages.length,
      messagesPending: messages.filter((m) => !m.handled).length,
      subscribers: subscribers.length,
      trustees: trustees.length,
      trusteesPub: trustees.filter((t) => t.published).length,
    }),
    [prayers, songs, schedule, reviews, testimonies, messages, subscribers, trustees]
  );

  /* ----- Reviews ----- */
  const toggleReviewPublished = async (item) => {
    try {
      const { data } = await api.patch(`/reviews/${item.id}`, { published: !item.published });
      setReviews((cur) => cur.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const deleteReview = async (item) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/reviews/${item.id}`);
      setReviews((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const saveReviewText = async (item, newText) => {
    try {
      const { data } = await api.patch(`/reviews/${item.id}`, {
        review_text: newText,
      });
      setReviews((cur) => cur.map((x) => (x.id === item.id ? data : x)));
      toast.success("Review text updated.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  /* ----- Testimonies ----- */
  const toggleTestimonyPublished = async (item) => {
    try {
      const { data } = await api.patch(`/testimonies/${item.id}`, { published: !item.published });
      setTestimonies((cur) => cur.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const updateFeaturedQuote = async (item, quote) => {
    try {
      const { data } = await api.patch(`/testimonies/${item.id}`, { featured_quote: quote });
      setTestimonies((cur) => cur.map((x) => (x.id === data.id ? data : x)));
      toast.success("Featured quote saved.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const updateTestimonyDetails = async (item, payload) => {
    try {
      const { data } = await api.patch(`/testimonies/${item.id}`, payload);
      setTestimonies((cur) => cur.map((x) => (x.id === data.id ? data : x)));
      toast.success("Testimony details saved.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const deleteTestimony = async (item) => {
    if (!window.confirm("Delete this testimony?")) return;
    try {
      await api.delete(`/testimonies/${item.id}`);
      setTestimonies((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  /* ----- Contact Messages ----- */
  const toggleMessageHandled = async (item) => {
    try {
      const { data } = await api.patch(`/contact-messages/${item.id}`, { handled: !item.handled });
      setMessages((cur) => cur.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const deleteMessage = async (item) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contact-messages/${item.id}`);
      setMessages((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  /* ----- Newsletter subscribers ----- */
  const deleteSubscriber = async (item) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/newsletter/${item.id}`);
      setSubscribers((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Removed.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const toggleHandled = async (kind, item) => {
    const url = kind === "prayer" ? "/prayer-requests" : "/song-requests";
    try {
      const { data } = await api.patch(`${url}/${item.id}`, { handled: !item.handled });
      if (kind === "prayer") {
        setPrayers((cur) => cur.map((x) => (x.id === data.id ? data : x)));
      } else {
        setSongs((cur) => cur.map((x) => (x.id === data.id ? data : x)));
      }
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const removeItem = async (kind, item) => {
    if (!window.confirm("Delete this entry?")) return;
    const url = kind === "prayer" ? "/prayer-requests" : "/song-requests";
    try {
      await api.delete(`${url}/${item.id}`);
      if (kind === "prayer") setPrayers((cur) => cur.filter((x) => x.id !== item.id));
      else setSongs((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const startNew = () => {
    setEditing("new");
    setDraft(emptySched);
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setDraft({
      title: item.title,
      host: item.host || "",
      day: item.day,
      start_time: item.start_time,
      end_time: item.end_time,
      description: item.description || "",
    });
  };

  const saveDraft = async () => {
    if (!draft.title.trim() || !draft.start_time || !draft.end_time) {
      toast.error("Title, start and end time are required.");
      return;
    }
    try {
      if (editing === "new") {
        const { data } = await api.post("/schedule", draft);
        setSchedule((cur) => [...cur, data]);
        toast.success("Show added.");
      } else {
        const { data } = await api.patch(`/schedule/${editing}`, draft);
        setSchedule((cur) => cur.map((x) => (x.id === data.id ? data : x)));
        toast.success("Show updated.");
      }
      setEditing(null);
      setDraft(emptySched);
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const removeSched = async (id) => {
    if (!window.confirm("Delete this show block?")) return;
    try {
      await api.delete(`/schedule/${id}`);
      setSchedule((cur) => cur.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const move = async (idx, dir) => {
    const newArr = [...schedule];
    const target = idx + dir;
    if (target < 0 || target >= newArr.length) return;
    [newArr[idx], newArr[target]] = [newArr[target], newArr[idx]];
    setSchedule(newArr);
    try {
      await api.post("/schedule/reorder", { ids: newArr.map((s) => s.id) });
    } catch (e) {
      toast.error(extractApiError(e));
      loadAll();
    }
  };

  /* ----- Trustees ----- */
  const startNewTrustee = () => {
    setTrusteeEditing("new");
    setTrusteeDraft(emptyTrustee);
  };
  const startEditTrustee = (item) => {
    setTrusteeEditing(item.id);
    setTrusteeDraft({
      name: item.name || "",
      role: item.role || "Trustee",
      bio: item.bio || "",
      image_url: item.image_url || "",
      published: !!item.published,
    });
  };
  const cancelTrustee = () => {
    setTrusteeEditing(null);
    setTrusteeDraft(emptyTrustee);
  };
  const saveTrustee = async () => {
    if (!trusteeDraft.name.trim() || !trusteeDraft.bio.trim()) {
      toast.error("Name and bio are required.");
      return;
    }
    try {
      if (trusteeEditing === "new") {
        const { data } = await api.post("/trustees", trusteeDraft);
        setTrustees((cur) => [...cur, data]);
        toast.success("Trustee added.");
      } else {
        const { data } = await api.patch(`/trustees/${trusteeEditing}`, trusteeDraft);
        setTrustees((cur) => cur.map((x) => (x.id === data.id ? data : x)));
        toast.success("Trustee updated.");
      }
      cancelTrustee();
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const toggleTrusteePublished = async (item) => {
    try {
      const { data } = await api.patch(`/trustees/${item.id}`, { published: !item.published });
      setTrustees((cur) => cur.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const deleteTrustee = async (item) => {
    if (!window.confirm(`Delete trustee "${item.name}"?`)) return;
    try {
      await api.delete(`/trustees/${item.id}`);
      setTrustees((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };
  const moveTrustee = async (idx, dir) => {
    const newArr = [...trustees];
    const target = idx + dir;
    if (target < 0 || target >= newArr.length) return;
    [newArr[idx], newArr[target]] = [newArr[target], newArr[idx]];
    setTrustees(newArr);
    try {
      await api.post("/trustees/reorder", { ids: newArr.map((t) => t.id) });
    } catch (e) {
      toast.error(extractApiError(e));
      loadAll();
    }
  };

  return (
    <main data-testid="admin-page" className="bg-canvas min-h-[80vh]">
      {/* Sub-header */}
      <div className="border-b border-[#1F2833] bg-[#0B0C10]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
              Control Room
            </span>
            <h1 className="font-display text-2xl sm:text-3xl text-white tracking-tight mt-1">
              Admin Studio
            </h1>
            <p className="text-xs text-[#C5D1EB]/70 mt-1">
              Signed in as {user?.email}
            </p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 text-[#C5D1EB] hover:text-white border border-[#C5D1EB]/40 hover:border-[#C5001A] px-4 py-2 text-sm transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        {[
          ["Prayer Requests", counts.prayer, `${counts.prayerPending} pending`, Heart, "stat-prayer"],
          ["Song Requests", counts.song, `${counts.songPending} pending`, Music2, "stat-song"],
          ["Lineup Blocks", counts.schedule, "this week", ListChecks, "stat-schedule"],
          ["Reviews", counts.reviews, `${counts.reviewsPub} published`, Star, "stat-reviews"],
          ["Testimonies", counts.testimonies, `${counts.testimoniesPub} featured`, Quote, "stat-testimonies"],
          ["Messages", counts.messages, `${counts.messagesPending} pending`, MessageSquare, "stat-messages"],
          ["Subscribers", counts.subscribers, "newsletter list", Mail, "stat-newsletter"],
          ["Trustees", counts.trustees, `${counts.trusteesPub} live on home`, Users, "stat-trustees"],
        ].map(([t, v, sub, Icon, tid]) => (
          <div key={t} data-testid={tid} className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                {t}
              </span>
              <Icon size={16} className="text-[#C5001A]" />
            </div>
            <p className="mt-3 font-display text-4xl text-white">{v}</p>
            <p className="mt-1 text-xs text-[#C5D1EB]/70">{sub}</p>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap gap-2 border-b border-[#1F2833]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#C5001A] text-white"
                  : "border-transparent text-[#C5D1EB] hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {loading ? (
          <p className="text-[#C5D1EB] text-sm">Loading…</p>
        ) : tab === "prayer" ? (
          <RequestTable
            kind="prayer"
            rows={prayers}
            columns={["name", "contact_number", "location", "request_text"]}
            headers={["Name", "Contact", "Location", "Request"]}
            onToggle={toggleHandled}
            onDelete={removeItem}
          />
        ) : tab === "song" ? (
          <RequestTable
            kind="song"
            rows={songs}
            columns={["listener_name", "city", "song_title", "dedicated_message"]}
            headers={["Listener", "City", "Song / Artist", "Message"]}
            onToggle={toggleHandled}
            onDelete={removeItem}
          />
        ) : tab === "schedule" ? (
          <ScheduleManager
            schedule={schedule}
            editing={editing}
            draft={draft}
            setDraft={setDraft}
            startNew={startNew}
            startEdit={startEdit}
            cancel={() => {
              setEditing(null);
              setDraft(emptySched);
            }}
            save={saveDraft}
            remove={removeSched}
            move={move}
          />
        ) : tab === "reviews" ? (
          <ReviewsManager
            rows={reviews}
            togglePublished={toggleReviewPublished}
            onDelete={deleteReview}
            saveText={saveReviewText}
          />
        ) : tab === "testimonies" ? (
          <TestimoniesManager
            rows={testimonies}
            togglePublished={toggleTestimonyPublished}
            saveFeatured={updateFeaturedQuote}
            saveDetails={updateTestimonyDetails}
            onDelete={deleteTestimony}
          />
        ) : tab === "messages" ? (
          <MessagesManager
            rows={messages}
            toggleHandled={toggleMessageHandled}
            onDelete={deleteMessage}
          />
        ) : tab === "newsletter" ? (
          <NewsletterManager
            rows={subscribers}
            onDelete={deleteSubscriber}
          />
        ) : (
          <TrusteesManager
            rows={trustees}
            editing={trusteeEditing}
            draft={trusteeDraft}
            setDraft={setTrusteeDraft}
            startNew={startNewTrustee}
            startEdit={startEditTrustee}
            cancel={cancelTrustee}
            save={saveTrustee}
            togglePublished={toggleTrusteePublished}
            remove={deleteTrustee}
            move={moveTrustee}
          />
        )}
      </section>
    </main>
  );
}

function RequestTable({ kind, rows, columns, headers, onToggle, onDelete }) {
  if (!rows.length) {
    return (
      <p
        data-testid={`${kind}-empty`}
        className="text-sm text-[#C5D1EB]/70 bg-[#1F2833] border border-[#C5D1EB]/15 p-6"
      >
        No {kind === "prayer" ? "prayer" : "song"} requests yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto border border-[#C5D1EB]/15">
      <table data-testid={`${kind}-table`} className="w-full text-sm">
        <thead className="bg-[#1F2833] text-[#C5D1EB]">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase w-12">
              Aired
            </th>
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase"
              >
                {h}
              </th>
            ))}
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
              When
            </th>
            <th className="px-4 py-3 w-10" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`${kind}-row-${r.id}`}
              className={`border-t border-[#C5D1EB]/10 ${
                r.handled ? "opacity-50" : ""
              } align-top bg-[#0B0C10]`}
            >
              <td className="px-4 py-3">
                <button
                  data-testid={`toggle-${kind}-${r.id}`}
                  onClick={() => onToggle(kind, r)}
                  aria-label="Toggle aired"
                  className={`inline-flex items-center justify-center w-7 h-7 border ${
                    r.handled
                      ? "border-[#C5001A] text-[#C5001A]"
                      : "border-[#C5D1EB]/40 text-[#C5D1EB] hover:text-white"
                  }`}
                >
                  {r.handled ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                </button>
              </td>
              {columns.map((c) => (
                <td key={c} className="px-4 py-3 text-white max-w-xs">
                  <span className="block whitespace-pre-wrap break-words">
                    {r[c] || <span className="text-[#C5D1EB]/40">—</span>}
                  </span>
                </td>
              ))}
              <td className="px-4 py-3 text-[#C5D1EB]/70 text-xs whitespace-nowrap">
                {new Date(r.created_at).toLocaleString("en-NZ", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </td>
              <td className="px-4 py-3">
                <button
                  data-testid={`delete-${kind}-${r.id}`}
                  onClick={() => onDelete(kind, r)}
                  className="text-[#C5D1EB]/60 hover:text-[#E31C3D]"
                  aria-label="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleManager({
  schedule,
  editing,
  draft,
  setDraft,
  startNew,
  startEdit,
  cancel,
  save,
  remove,
  move,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#C5D1EB]">
          Build & order the weekly broadcast lineup.
        </p>
        {editing == null && (
          <button
            data-testid="schedule-new"
            onClick={startNew}
            className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-4 py-2 text-sm"
          >
            <Plus size={15} /> Add Show
          </button>
        )}
      </div>

      {editing != null && (
        <div
          data-testid="schedule-editor"
          className="bg-[#1F2833] border border-[#C5D1EB]/15 p-6 mb-6 grid sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Title
            </label>
            <input
              data-testid="sched-title"
              className="brand-input"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Artist / Speaker
            </label>
            <input
              data-testid="sched-host"
              className="brand-input"
              placeholder="e.g. Dr. Paul Dhinakaran"
              value={draft.host}
              onChange={(e) => setDraft({ ...draft, host: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Day (or “Daily”)
            </label>
            <select
              data-testid="sched-day"
              className="brand-input"
              value={draft.day}
              onChange={(e) => setDraft({ ...draft, day: e.target.value })}
            >
              {DAYS.map((d) => (
                <option key={d} value={d} className="bg-[#0B0C10] text-white">
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Start Time
            </label>
            <input
              data-testid="sched-start"
              type="time"
              className="brand-input"
              value={draft.start_time}
              onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              End Time
            </label>
            <input
              data-testid="sched-end"
              type="time"
              className="brand-input"
              value={draft.end_time}
              onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Description
            </label>
            <textarea
              data-testid="sched-description"
              rows={3}
              className="brand-input"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <button
              data-testid="sched-save"
              onClick={save}
              className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-5 py-2.5 text-sm"
            >
              <Save size={14} /> {editing === "new" ? "Create" : "Update"}
            </button>
            <button
              data-testid="sched-cancel"
              onClick={cancel}
              className="inline-flex items-center gap-2 border border-[#C5D1EB]/40 text-[#C5D1EB] hover:text-white px-5 py-2.5 text-sm"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {schedule.length === 0 ? (
        <p
          data-testid="schedule-empty"
          className="text-sm text-[#C5D1EB]/70 bg-[#1F2833] border border-[#C5D1EB]/15 p-6"
        >
          No shows scheduled. Add the first one to begin.
        </p>
      ) : (
        <ul className="divide-y divide-[#C5D1EB]/10 border border-[#C5D1EB]/15">
          {schedule.map((s, i) => (
            <li
              key={s.id}
              data-testid={`schedule-row-${s.id}`}
              className="bg-[#1F2833] px-5 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4"
            >
              <div className="flex items-start gap-4">
                <Calendar size={18} className="text-[#C5001A] mt-1" />
                <div>
                  <p className="font-display text-white text-lg leading-tight">
                    {s.title}
                  </p>
                  <p className="mt-1 text-xs text-[#C5D1EB]">
                    {s.day} · {s.start_time}–{s.end_time}
                    {s.host ? ` · ${s.host}` : ""}
                  </p>
                  {s.description && (
                    <p className="mt-1 text-xs text-[#C5D1EB]/70 max-w-xl">
                      {s.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`sched-up-${s.id}`}
                  onClick={() => move(i, -1)}
                  className="p-2 border border-[#C5D1EB]/30 text-[#C5D1EB] hover:text-white hover:border-[#C5001A]"
                  aria-label="Move up"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  data-testid={`sched-down-${s.id}`}
                  onClick={() => move(i, 1)}
                  className="p-2 border border-[#C5D1EB]/30 text-[#C5D1EB] hover:text-white hover:border-[#C5001A]"
                  aria-label="Move down"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  data-testid={`sched-edit-${s.id}`}
                  onClick={() => startEdit(s)}
                  className="px-3 py-2 text-xs border border-[#C5D1EB]/30 text-[#C5D1EB] hover:text-white hover:border-[#C5001A]"
                >
                  Edit
                </button>
                <button
                  data-testid={`sched-delete-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="p-2 text-[#C5D1EB]/60 hover:text-[#E31C3D]"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Reviews manager                                                            */
/* -------------------------------------------------------------------------- */
function ReviewsManager({ rows, togglePublished, onDelete, saveText }) {
  if (!rows.length) {
    return (
      <p
        data-testid="reviews-empty"
        className="text-sm text-[#C5D1EB]/70 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
      >
        No reviews submitted yet.
      </p>
    );
  }
  return (
    <ul className="grid md:grid-cols-2 gap-4">
      {rows.map((r) => (
        <ReviewAdminRow
          key={r.id}
          r={r}
          togglePublished={togglePublished}
          onDelete={onDelete}
          saveText={saveText}
        />
      ))}
    </ul>
  );
}

function ReviewAdminRow({ r, togglePublished, onDelete, saveText }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.review_text);

  const onSave = async () => {
    const t = draft.trim();
    if (t.length < 8) return;
    await saveText(r, t);
    setEditing(false);
  };

  return (
    <li
      data-testid={`review-row-${r.id}`}
      className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-5 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[#C5001A]">
          {[...Array(r.rating || 5)].map((_, i) => (
            <Star key={i} size={12} fill="currentColor" />
          ))}
        </div>
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#C5D1EB]/70">
          {r.published ? "Published" : "Hidden"}
          {r.consent_to_edit ? " · Consent ✓" : " · No consent"}
        </span>
      </div>

      {editing ? (
        <textarea
          data-testid={`review-edit-${r.id}`}
          className="brand-input mt-3 flex-1 min-h-[120px]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : (
        <p className="mt-3 text-sm text-white leading-relaxed flex-1">
          "{r.review_text}"
        </p>
      )}

      <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70">
        {r.name}
        {r.location ? ` · ${r.location}` : ""} ·{" "}
        {new Date(r.created_at).toLocaleString("en-NZ", {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </p>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <button
          data-testid={`review-toggle-${r.id}`}
          onClick={() => togglePublished(r)}
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs border ${
            r.published
              ? "bg-[#C5001A] border-[#C5001A] text-white"
              : "border-white/20 text-[#C5D1EB] hover:text-white"
          }`}
        >
          {r.published ? <Eye size={13} /> : <EyeOff size={13} />}
          {r.published ? "Published" : "Publish"}
        </button>

        {editing ? (
          <>
            <button
              data-testid={`review-save-${r.id}`}
              onClick={onSave}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#C5001A] bg-[#C5001A]/15 text-white hover:bg-[#C5001A]"
            >
              <Save size={13} /> Save text
            </button>
            <button
              data-testid={`review-cancel-${r.id}`}
              onClick={() => {
                setDraft(r.review_text);
                setEditing(false);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-white/20 text-[#C5D1EB] hover:text-white"
            >
              <X size={13} /> Cancel
            </button>
          </>
        ) : (
          <button
            data-testid={`review-edit-btn-${r.id}`}
            onClick={() => {
              setDraft(r.review_text);
              setEditing(true);
            }}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-white/20 text-[#C5D1EB] hover:text-white"
          >
            Edit text
          </button>
        )}

        <button
          data-testid={`review-delete-${r.id}`}
          onClick={() => onDelete(r)}
          className="ml-auto p-2 text-[#C5D1EB]/60 hover:text-[#E31C3D]"
          aria-label="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/*  Testimonies manager                                                        */
/* -------------------------------------------------------------------------- */
function TestimoniesManager({ rows, togglePublished, saveFeatured, saveDetails, onDelete }) {
  if (!rows.length) {
    return (
      <p
        data-testid="testimonies-empty"
        className="text-sm text-[#C5D1EB]/70 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
      >
        No testimonies submitted yet.
      </p>
    );
  }
  return (
    <ul className="space-y-4">
      {rows.map((t) => (
        <TestimonyAdminRow
          key={t.id}
          item={t}
          togglePublished={togglePublished}
          saveFeatured={saveFeatured}
          saveDetails={saveDetails}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}

function TestimonyAdminRow({ item, togglePublished, saveFeatured, saveDetails, onDelete }) {
  const [quote, setQuote] = useState(item.featured_quote || "");
  const [name, setName] = useState(item.name || "");
  const [location, setLocation] = useState(item.location || "");
  const [body, setBody] = useState(item.testimony_text || "");
  const detailsDirty =
    name.trim() !== (item.name || "") ||
    location.trim() !== (item.location || "") ||
    body.trim() !== (item.testimony_text || "");

  const handleSaveDetails = () => {
    const payload = {};
    if (name.trim() !== (item.name || "")) payload.name = name.trim();
    if (location.trim() !== (item.location || "")) payload.location = location.trim();
    if (body.trim() !== (item.testimony_text || "")) payload.testimony_text = body.trim();
    if (Object.keys(payload).length === 0) return;
    saveDetails(item, payload);
  };

  return (
    <li
      data-testid={`testimony-row-${item.id}`}
      className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70">
          {new Date(item.created_at).toLocaleString("en-NZ", {
            dateStyle: "medium",
            timeStyle: "short",
          })}{" "}
          ·{" "}
          <span className={item.consent_to_broadcast ? "text-[#C5001A]" : ""}>
            {item.consent_to_broadcast
              ? "Broadcast consent ✓"
              : "No broadcast consent"}
          </span>
        </p>
        <span
          className={`text-[10px] tracking-[0.22em] uppercase ${
            item.published ? "text-[#C5001A]" : "text-[#C5D1EB]/60"
          }`}
        >
          {item.published ? "Featured" : "Hidden"}
        </span>
      </div>

      {/* Editable name + location */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
            Name (shown publicly)
          </label>
          <input
            data-testid={`testimony-name-${item.id}`}
            className="brand-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jeremiah David"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
            Location (city / region)
          </label>
          <input
            data-testid={`testimony-location-${item.id}`}
            className="brand-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. India"
          />
        </div>
      </div>

      {/* Editable testimony body */}
      <div className="mt-4">
        <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
          Full testimony
        </label>
        <textarea
          data-testid={`testimony-body-${item.id}`}
          rows={5}
          className="brand-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      {item.contact && (
        <p className="mt-3 text-[11px] text-[#C5D1EB]/70">
          Submitted contact: {item.contact}
        </p>
      )}

      {/* Featured public quote */}
      <div className="mt-5">
        <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
          Featured quote (shown on the public Testimonies page)
        </label>
        <textarea
          data-testid={`testimony-quote-${item.id}`}
          rows={2}
          className="brand-input"
          placeholder="Optional short quote to feature publicly…"
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          data-testid={`testimony-save-details-${item.id}`}
          onClick={handleSaveDetails}
          disabled={!detailsDirty || !name.trim() || !body.trim()}
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs border transition-colors ${
            detailsDirty && name.trim() && body.trim()
              ? "bg-[#C5001A] border-[#C5001A] text-white hover:bg-[#E31C3D]"
              : "border-white/15 text-[#C5D1EB]/40 cursor-not-allowed"
          }`}
        >
          <Save size={13} /> Save details
        </button>
        <button
          data-testid={`testimony-save-${item.id}`}
          onClick={() => saveFeatured(item, quote)}
          className="inline-flex items-center gap-2 border border-white/20 hover:border-[#C5001A] text-[#C5D1EB] hover:text-white px-3 py-2 text-xs transition-colors"
        >
          <Save size={13} /> Save quote
        </button>
        <button
          data-testid={`testimony-toggle-${item.id}`}
          onClick={() => togglePublished(item)}
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs border ${
            item.published
              ? "bg-[#C5001A] border-[#C5001A] text-white"
              : "border-white/20 text-[#C5D1EB] hover:text-white"
          }`}
        >
          {item.published ? <Eye size={13} /> : <EyeOff size={13} />}
          {item.published ? "Featured" : "Feature publicly"}
        </button>
        <button
          data-testid={`testimony-delete-${item.id}`}
          onClick={() => onDelete(item)}
          className="ml-auto p-2 text-[#C5D1EB]/60 hover:text-[#E31C3D]"
          aria-label="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/*  Contact-messages manager                                                   */
/* -------------------------------------------------------------------------- */
function MessagesManager({ rows, toggleHandled, onDelete }) {
  if (!rows.length) {
    return (
      <p
        data-testid="messages-empty"
        className="text-sm text-[#C5D1EB]/70 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
      >
        No contact messages yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto border border-white/10">
      <table data-testid="messages-table" className="w-full text-sm">
        <thead className="bg-white/[0.04] text-[#C5D1EB]">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase w-12">
              Done
            </th>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
              From
            </th>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
              Subject
            </th>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
              Message
            </th>
            <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
              When
            </th>
            <th className="px-4 py-3 w-10" />
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr
              key={m.id}
              data-testid={`message-row-${m.id}`}
              className={`border-t border-white/10 align-top bg-[#0B0C10] ${
                m.handled ? "opacity-50" : ""
              }`}
            >
              <td className="px-4 py-3">
                <button
                  data-testid={`message-toggle-${m.id}`}
                  onClick={() => toggleHandled(m)}
                  aria-label="Toggle handled"
                  className={`inline-flex items-center justify-center w-7 h-7 border ${
                    m.handled
                      ? "border-[#C5001A] text-[#C5001A]"
                      : "border-white/20 text-[#C5D1EB] hover:text-white"
                  }`}
                >
                  {m.handled ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                </button>
              </td>
              <td className="px-4 py-3 text-white">
                <p className="font-medium">{m.name}</p>
                <p className="text-[11px] text-[#C5D1EB]/70 break-all">{m.email}</p>
                {m.phone && (
                  <p className="text-[11px] text-[#C5D1EB]/70">{m.phone}</p>
                )}
              </td>
              <td className="px-4 py-3 text-white">
                {m.subject || <span className="text-[#C5D1EB]/40">—</span>}
              </td>
              <td className="px-4 py-3 text-white max-w-md">
                <span className="block whitespace-pre-wrap break-words">{m.message}</span>
              </td>
              <td className="px-4 py-3 text-[#C5D1EB]/70 text-xs whitespace-nowrap">
                {new Date(m.created_at).toLocaleString("en-NZ", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </td>
              <td className="px-4 py-3">
                <button
                  data-testid={`message-delete-${m.id}`}
                  onClick={() => onDelete(m)}
                  className="text-[#C5D1EB]/60 hover:text-[#E31C3D]"
                  aria-label="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Newsletter subscribers manager                                             */
/* -------------------------------------------------------------------------- */
function NewsletterManager({ rows, onDelete }) {
  if (!rows.length) {
    return (
      <p
        data-testid="newsletter-empty"
        className="text-sm text-[#C5D1EB]/70 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
      >
        No newsletter subscribers yet.
      </p>
    );
  }
  const exportCsv = () => {
    const header = "name,email,source,subscribed_at\n";
    const body = rows
      .map((r) =>
        [
          `"${(r.name || "").replace(/"/g, '""')}"`,
          `"${r.email}"`,
          `"${r.source || ""}"`,
          `"${new Date(r.created_at).toISOString()}"`,
        ].join(",")
      )
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lifetalk-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#C5D1EB]">
          {rows.length} subscriber{rows.length === 1 ? "" : "s"} on the
          stay-in-touch list.
        </p>
        <button
          data-testid="newsletter-export-csv"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 border border-white/20 hover:border-[#C5001A] text-[#C5D1EB] hover:text-white px-4 py-2 text-xs transition-colors"
        >
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table data-testid="newsletter-table" className="w-full text-sm">
          <thead className="bg-white/[0.04] text-[#C5D1EB]">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
                Email
              </th>
              <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
                Source
              </th>
              <th className="px-4 py-3 text-left font-medium text-[11px] tracking-[0.2em] uppercase">
                Subscribed
              </th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr
                key={s.id}
                data-testid={`subscriber-row-${s.id}`}
                className="border-t border-white/10 bg-[#0B0C10]"
              >
                <td className="px-4 py-3 text-white">{s.name}</td>
                <td className="px-4 py-3 text-white break-all">{s.email}</td>
                <td className="px-4 py-3 text-[#C5D1EB]/80 text-xs">
                  {s.source || "—"}
                </td>
                <td className="px-4 py-3 text-[#C5D1EB]/70 text-xs whitespace-nowrap">
                  {new Date(s.created_at).toLocaleString("en-NZ", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`subscriber-delete-${s.id}`}
                    onClick={() => onDelete(s)}
                    className="text-[#C5D1EB]/60 hover:text-[#E31C3D]"
                    aria-label="Delete subscriber"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Trustees manager                                                           */
/* -------------------------------------------------------------------------- */
function TrusteesManager({
  rows,
  editing,
  draft,
  setDraft,
  startNew,
  startEdit,
  cancel,
  save,
  togglePublished,
  remove,
  move,
}) {
  return (
    <div data-testid="trustees-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#C5D1EB]">
          Add, edit and reorder trustees featured on the Home page.
        </p>
        {editing == null && (
          <button
            data-testid="trustee-new"
            onClick={startNew}
            className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-4 py-2 text-sm"
          >
            <Plus size={15} /> Add Trustee
          </button>
        )}
      </div>

      {editing != null && (
        <div
          data-testid="trustee-editor"
          className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-6 mb-6 grid sm:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Full name
            </label>
            <input
              data-testid="trustee-name"
              className="brand-input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Role / Title
            </label>
            <input
              data-testid="trustee-role"
              className="brand-input"
              placeholder="Trustee"
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Image URL
            </label>
            <div className="flex items-center gap-3">
              {draft.image_url ? (
                <img
                  src={draft.image_url}
                  alt=""
                  className="w-14 h-14 object-cover object-top border border-white/15"
                />
              ) : (
                <span className="w-14 h-14 flex items-center justify-center border border-white/15 text-[#C5D1EB]/60">
                  <ImagePlus size={18} />
                </span>
              )}
              <input
                data-testid="trustee-image"
                className="brand-input flex-1"
                placeholder="https://…"
                value={draft.image_url}
                onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Biography
            </label>
            <textarea
              data-testid="trustee-bio"
              rows={6}
              className="brand-input"
              value={draft.bio}
              onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-[#C5D1EB]">
              <input
                data-testid="trustee-published"
                type="checkbox"
                checked={!!draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              />
              Published on Home page
            </label>
            <div className="flex-1" />
            <button
              data-testid="trustee-save"
              onClick={save}
              className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-5 py-2.5 text-sm"
            >
              <Save size={14} /> {editing === "new" ? "Create" : "Update"}
            </button>
            <button
              data-testid="trustee-cancel"
              onClick={cancel}
              className="inline-flex items-center gap-2 border border-white/20 text-[#C5D1EB] hover:text-white px-5 py-2.5 text-sm"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p
          data-testid="trustees-empty"
          className="text-sm text-[#C5D1EB]/70 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
        >
          No trustees yet. Add the first board member to begin.
        </p>
      ) : (
        <ul className="grid md:grid-cols-2 gap-4">
          {rows.map((t, i) => (
            <li
              key={t.id}
              data-testid={`trustee-row-${t.id}`}
              className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-5 flex gap-4"
            >
              {t.image_url ? (
                <img
                  src={t.image_url}
                  alt={t.name}
                  className="w-20 h-24 object-cover object-top border border-white/15 shrink-0"
                />
              ) : (
                <span className="w-20 h-24 flex items-center justify-center border border-white/15 text-[#C5D1EB]/60 shrink-0">
                  <ImagePlus size={20} />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg text-white tracking-tight leading-tight">
                      {t.name}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5D1EB]/70 mt-1">
                      {t.role || "Trustee"}
                    </p>
                  </div>
                  <span
                    className={`text-[10px] tracking-[0.22em] uppercase ${
                      t.published ? "text-[#C5001A]" : "text-[#C5D1EB]/50"
                    }`}
                  >
                    {t.published ? "Live" : "Hidden"}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#C5D1EB] leading-relaxed line-clamp-4">
                  {t.bio}
                </p>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`trustee-up-${t.id}`}
                    onClick={() => move(i, -1)}
                    className="p-2 border border-white/20 text-[#C5D1EB] hover:text-white hover:border-[#C5001A]"
                    aria-label="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    data-testid={`trustee-down-${t.id}`}
                    onClick={() => move(i, 1)}
                    className="p-2 border border-white/20 text-[#C5D1EB] hover:text-white hover:border-[#C5001A]"
                    aria-label="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    data-testid={`trustee-toggle-${t.id}`}
                    onClick={() => togglePublished(t)}
                    className={`inline-flex items-center gap-2 px-3 py-2 text-xs border ${
                      t.published
                        ? "bg-[#C5001A] border-[#C5001A] text-white"
                        : "border-white/20 text-[#C5D1EB] hover:text-white"
                    }`}
                  >
                    {t.published ? <Eye size={13} /> : <EyeOff size={13} />}
                    {t.published ? "Live" : "Publish"}
                  </button>
                  <button
                    data-testid={`trustee-edit-${t.id}`}
                    onClick={() => startEdit(t)}
                    className="px-3 py-2 text-xs border border-white/20 text-[#C5D1EB] hover:text-white"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`trustee-delete-${t.id}`}
                    onClick={() => remove(t)}
                    className="ml-auto p-2 text-[#C5D1EB]/60 hover:text-[#E31C3D]"
                    aria-label="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
